import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ICalendarAccount, ICalendarSyncSummary, ICrmCalendar, ICrmError, IMailSyncSummary } from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type CrmCalendarKeyFunctions = {
    listAccounts: () => Promise<ICalendarAccount[] | ICrmError>;
    /** The Google consent URL. The caller navigates the whole window there; Google sends it back to the inbox. `withMail` also asks for Gmail read access. */
    connectUrl: (withMail?: boolean) => Promise<string | ICrmError>;
    setMailSync: (accountId: number, enabled: boolean) => Promise<true | ICrmError>;
    syncMailNow: (accountId: number) => Promise<IMailSyncSummary | ICrmError>;
    listCalendars: (accountId: number) => Promise<ICrmCalendar[] | ICrmError>;
    /** The full set of calendars that count as busy; anything omitted is deselected. */
    selectCalendars: (accountId: number, googleCalendarIds: string[]) => Promise<ICrmCalendar[] | ICrmError>;
    setBookingCalendar: (accountId: number, googleCalendarId: string | null) => Promise<true | ICrmError>;
    syncNow: (accountId: number) => Promise<ICalendarSyncSummary | ICrmError>;
    disconnect: (accountId: number) => Promise<true | ICrmError>;
};

/** A rep's Google Calendar connections. Tokens never reach the browser. */
export const useCrmCalendar = (): [CrmCalendarKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return {
            ok: false,
            status,
            message: err?.response?.data?.message
                || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        };
    };

    const listAccounts = async (): Promise<ICalendarAccount[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICalendarAccount[]> = await axios.get(`${crmUrl}/calendar/accounts`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const connectUrl = async (withMail = false): Promise<string | ICrmError> => {
        try {
            const result: AxiosResponse<{ url: string }> = await axios.get(`${crmUrl}/calendar/google/connect${withMail ? '?mail=1' : ''}`, { headers });
            return result.data.url;
        } catch (err: any) { return handle(err); }
    };

    const listCalendars = async (accountId: number): Promise<ICrmCalendar[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmCalendar[]> = await axios.get(`${crmUrl}/calendar/accounts/${accountId}/calendars`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const selectCalendars = async (accountId: number, googleCalendarIds: string[]): Promise<ICrmCalendar[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmCalendar[]> =
                await axios.put(`${crmUrl}/calendar/accounts/${accountId}/calendars`, { selected: googleCalendarIds }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setBookingCalendar = async (accountId: number, googleCalendarId: string | null): Promise<true | ICrmError> => {
        try {
            await axios.put(`${crmUrl}/calendar/accounts/${accountId}/booking-calendar`, { google_calendar_id: googleCalendarId }, { headers });
            return true;
        } catch (err: any) { return handle(err); }
    };

    const syncNow = async (accountId: number): Promise<ICalendarSyncSummary | ICrmError> => {
        try {
            const result: AxiosResponse<ICalendarSyncSummary> = await axios.post(`${crmUrl}/calendar/accounts/${accountId}/sync`, {}, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setMailSync = async (accountId: number, enabled: boolean): Promise<true | ICrmError> => {
        try {
            await axios.put(`${crmUrl}/calendar/accounts/${accountId}/mail`, { enabled }, { headers });
            return true;
        } catch (err: any) { return handle(err); }
    };

    const syncMailNow = async (accountId: number): Promise<IMailSyncSummary | ICrmError> => {
        try {
            const result: AxiosResponse<IMailSyncSummary> = await axios.post(`${crmUrl}/calendar/accounts/${accountId}/mail/sync`, {}, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const disconnect = async (accountId: number): Promise<true | ICrmError> => {
        try {
            await axios.delete(`${crmUrl}/calendar/accounts/${accountId}`, { headers });
            return true;
        } catch (err: any) { return handle(err); }
    };

    return [{ listAccounts, connectUrl, listCalendars, selectCalendars, setBookingCalendar, syncNow, setMailSync, syncMailNow, disconnect }];
};
